import React from 'react';

import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

// Skeleton scaffold that mirrors the live AnalyticsMetricsView layout
// (filters bar → KPI grid → 2×2 chart grid → question rates → call breakdown).
// Renders while org/programs are resolving so the user sees the shape of
// the page instead of a "Loading…" string.

export const AnalyticsLoadingSkeleton: React.FC = () => {
  return (
    <div
      className="flex flex-col gap-4"
      role="status"
      aria-busy="true"
      aria-label="Loading analytics dashboard"
    >
      {/* Filter row: program select + date range */}
      <div className="flex flex-wrap items-center gap-3">
        <Skeleton className="h-9 w-44" />
        <Skeleton className="h-9 w-56" />
      </div>

      {/* KPI grid — 6 vertical cards (Org tab shape) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="p-4 flex flex-col gap-3 shadow-sm">
            <div className="flex items-center gap-2">
              <Skeleton className="h-7 w-7 rounded-md" />
              <Skeleton className="h-3 w-24" />
            </div>
            <Skeleton className="h-6 w-28" />
            <Skeleton className="h-3 w-20" />
          </Card>
        ))}
      </div>

      {/* Two rows of two charts each */}
      {Array.from({ length: 2 }).map((_, row) => (
        <div key={row} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.from({ length: 2 }).map((__, col) => (
            <Card key={col} className="p-4 flex flex-col gap-4 shadow-sm">
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-8 w-24 rounded-md" />
              </div>
              <Skeleton className="h-[220px] w-full" />
            </Card>
          ))}
        </div>
      ))}

      {/* Question pass rates list */}
      <Card className="p-4 flex flex-col gap-3 shadow-sm">
        <Skeleton className="h-4 w-56" />
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-7 w-20 rounded-md" />
          ))}
        </div>
        <div className="flex flex-col gap-2 pt-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <Skeleton className="h-3 w-72" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-2 w-full" />
            </div>
          ))}
        </div>
      </Card>

      {/* Call breakdown — three cards side by side */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="p-4 flex flex-col gap-3 shadow-sm">
            <Skeleton className="h-4 w-32" />
            <div className="flex flex-col gap-2">
              {Array.from({ length: 3 }).map((__, j) => (
                <div key={j} className="flex flex-col gap-1">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-5 w-3/4 rounded-md" />
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
